import React, { ReactNode, Component, ComponentClass, FC } from 'react';
import './design-system/styles/components/error-boundaries.css';

// Interface tanımlamaları
interface ErrorBoundaryProps {
  children: ReactNode;
  pageName?: string;
  componentName?: string;
  formName?: string;
  tableName?: string;
  chartName?: string;
  modalName?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error | null;
  errorInfo?: any;
  hasNetworkError?: boolean;
}

interface GlobalErrorBoundaryState extends ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: any | null;
}

interface WithErrorBoundaryProps {
  [key: string]: any;
}

interface UseErrorBoundaryReturn {
  captureError: (error: Error) => void;
  resetError: () => void;
}

// Page Error Boundary
export function PageErrorBoundary({ children }: ErrorBoundaryProps): React.ReactElement {
  return (
    <div className="page-error-boundary">
      {children}
    </div>
  );
}

// Component Error Boundary
export function ComponentErrorBoundary({ children }: ErrorBoundaryProps): React.ReactElement {
  return (
    <div className="component-error-boundary">
      {children}
    </div>
  );
}

// Form Error Boundary
export function FormErrorBoundary({ children }: ErrorBoundaryProps): React.ReactElement {
  return (
    <div className="form-error-boundary">
      {children}
    </div>
  );
}

// Table Error Boundary
export function TableErrorBoundary({ children }: ErrorBoundaryProps): React.ReactElement {
  return (
    <div className="table-error-boundary">
      {children}
    </div>
  );
}

// Chart Error Boundary
export function ChartErrorBoundary({ children }: ErrorBoundaryProps): React.ReactElement {
  return (
    <div className="chart-error-boundary">
      {children}
    </div>
  );
}

// Modal Error Boundary
export function ModalErrorBoundary({ children }: ErrorBoundaryProps): React.ReactElement {
  return (
    <div className="modal-error-boundary">
      {children}
    </div>
  );
}

// Global Error Boundary for the entire app
export class GlobalErrorBoundary extends Component<{ children: ReactNode }, GlobalErrorBoundaryState> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(_error: Error): { hasError: boolean } {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, errorInfo: any): void {
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
    
    // Log error to console in development
    
    // In production, you might want to send this to an error reporting service
    // Example: errorReportingService.captureException(error, { extra: errorInfo });
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleGoHome = (): void => {
    window.location.href = '/';
  };

  override render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="global-error-boundary">
          <div className="global-error-boundary-content">
            <div className="global-error-boundary-icon">⚠️</div>
            <h1>Uygulamada Bir Hata Oluştu</h1>
            <p>Üzgünüz, beklenmeyen bir hata meydana geldi. Lütfen sayfayı yenileyin veya ana sayfaya dönün.</p>
            <div className="global-error-boundary-actions">
              <button className="global-error-btn global-error-btn-retry" onClick={this.handleRetry}>
                🔄 Sayfayı Yenile
              </button>
              <button className="global-error-btn global-error-btn-home" onClick={this.handleGoHome}>
                🏠 Ana Sayfaya Dön
              </button>
            </div>
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="global-error-details">
                <summary>Hata Detayları (Geliştirme Modu)</summary>
                <pre className="global-error-stack">
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Network Error Boundary
export class NetworkErrorBoundary extends Component<{ children: ReactNode }, { hasNetworkError: boolean }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasNetworkError: false };
  }

  override componentDidMount(): void {
    // Listen for online/offline events
    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);
  }

  override componentWillUnmount(): void {
    window.removeEventListener('online', this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);
  }

  handleOnline = (): void => {
    this.setState({ hasNetworkError: false });
  };

  handleOffline = (): void => {
    this.setState({ hasNetworkError: true });
  };

  override render(): ReactNode {
    if (this.state.hasNetworkError) {
      return (
        <div className="network-error-boundary">
          <div className="network-error-boundary-content">
            <div className="network-error-boundary-icon">📡</div>
            <h2>İnternet Bağlantısı Yok</h2>
            <p>İnternet bağlantınızı kontrol edin ve tekrar deneyin.</p>
            <button 
              className="network-error-btn" 
              onClick={() => window.location.reload()}
            >
              🔄 Tekrar Dene
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Async Error Boundary for async operations
export class AsyncErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean; error: Error | null }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): { hasError: boolean; error: Error } {
    return { hasError: true, error };
  }

  override componentDidCatch(_error: Error, _errorInfo: any): void {
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
  };

  override render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="async-error-boundary">
          <div className="async-error-boundary-content">
            <div className="async-error-boundary-icon">⏳</div>
            <h3>İşlem Hatası</h3>
            <p>Bir işlem sırasında hata oluştu. Lütfen tekrar deneyin.</p>
            <button className="async-error-btn" onClick={this.handleRetry}>
              🔄 Tekrar Dene
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Error Boundary HOC
export function withErrorBoundary<P extends WithErrorBoundaryProps>(
  WrappedComponent: ComponentClass<P> | FC<P>,
  _errorBoundaryProps: Partial<ErrorBoundaryProps> = {}
): FC<P> {
  const WithErrorBoundaryComponent: FC<P> = (props) => (
    <div className="error-boundary-wrapper">
      <WrappedComponent {...props as P} />
    </div>
  );

  WithErrorBoundaryComponent.displayName = `withErrorBoundary(${WrappedComponent.displayName || WrappedComponent.name})`;

  return WithErrorBoundaryComponent;
}

// Error Boundary Hook
export function useErrorBoundary(): UseErrorBoundaryReturn {
  const [error, setError] = React.useState<Error | null>(null);

  const resetError = React.useCallback((): void => {
    setError(null);
  }, []);

  const captureError = React.useCallback((error: Error): void => {
    setError(error);
  }, []);

  React.useEffect(() => {
    if (error) {
      throw error;
    }
  }, [error]);

  return { captureError, resetError };
}

const ErrorBoundaries = {
  PageErrorBoundary,
  ComponentErrorBoundary,
  FormErrorBoundary,
  TableErrorBoundary,
  ChartErrorBoundary,
  ModalErrorBoundary,
  GlobalErrorBoundary,
  NetworkErrorBoundary,
  AsyncErrorBoundary,
  withErrorBoundary,
  useErrorBoundary
};

export default ErrorBoundaries;